import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { FaMicrophone, FaPlay, FaPause, FaCheck } from "react-icons/fa";
import { Tooltip } from "react-tooltip";

import initialData, { Question } from "../../constants/data";
import * as AiLoader from "../../assets/ai-loading.json";
import useAudioRecording from "../../hooks/useAudioRecording";

import "react-tooltip/dist/react-tooltip.css";
import "./form-styles.css";
import Lottie from "lottie-react";

// Type casting for React icons
const FaMicrophoneIcon = FaMicrophone as any;
const FaPlayIcon = FaPlay as any;
const FaPauseIcon = FaPause as any;
const FaCheckIcon = FaCheck as any;
const IoIosInformationCircleOutlineIcon = IoIosInformationCircleOutline as any;

// Generate validation schema using Yup
const generateValidationSchema = (data: Question[]) => {
  const schema: Record<string, any> = {};

  data.forEach((question) => {
    const { question_id, question_type, validation_rules } = question;

    if (question_type === "multi-select") {
      schema[question_id] = Yup.array()
        .of(Yup.string())
        .min(1, "At least one profession must be selected");
      return;
    }

    if (!validation_rules.length) return;

    if (validation_rules.length === 1) {
      const rule = validation_rules[0];

      if (rule.type === "required") {
        schema[question_id] = Yup.string().required(rule.error_message);
      } else if (rule.type === "min_length") {
        schema[question_id] = Yup.string().min(rule.value!, rule.error_message);
      } else if (rule.type === "max_length") {
        schema[question_id] = Yup.string().max(rule.value!, rule.error_message);
      } else if (rule.type === "number") {
        schema[question_id] = Yup.number()
          .typeError(rule.error_message)
          .required(rule.error_message);
      }
      return;
    }

    // Multiple validations
    schema[question_id] = Yup.string().test(
      "validations",
      "Invalid input",
      (value) => {
        return validation_rules.every((rule) => {
          if (rule.type === "required" && (!value || value.trim() === "")) {
            return false;
          }
          if (
            rule.type === "min_length" &&
            value &&
            value.length < rule.value!
          ) {
            return false;
          }
          if (
            rule.type === "max_length" &&
            value &&
            value.length > rule.value!
          ) {
            return false;
          }
          if (rule.type === "number" && isNaN(Number(value))) {
            return false;
          }
          return true;
        });
      }
    );
  });

  return Yup.object().shape(schema);
};

const API_AUTH_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyZXN1bHQiOnsiZG9jdG9yX3VuaXF1ZV9pZCI6InVRTVlHSEo1am5tZ1BxOSIsIm1vYmlsZV9ubyI6IjEyMzEyMzEyMzEiLCJjbGluaWNfaWQiOiI3NyIsImhvc3BpdGFsX2J1c2luZXNzX2lkIjoiMzk2NDU4MTI3IiwidXNlcl9pZCI6NjR9LCJpYXQiOjE3MTY4OTk4MzgsImV4cCI6MTc0ODQ1NzQzOH0.oMscweVhj9NOGC_6JN3TQa20NImu620Uy3jc6l4wpe8";
const API_URL = "https://pm-tatva-agents-uat.tatvacare.in/api/v1/agents/ner";

// Component to auto-fill answers
const AutoFillAnswers = ({ data }: { data: Question[] }) => {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    data.forEach((question) => {
      if (question.answer) {
        setFieldValue(question.question_id.toString(), question.answer);
      }
    });
  }, [data, setFieldValue]);

  return null;
};

// Recording control buttons component
const RecordingControls = ({
  audioBlob,
  isRecording,
  isPaused,
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording,
  deleteRecording,
  timer,
}: any) => (
  <div
    style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
  >
    {!audioBlob ? (
      !isRecording ? (
        <button className="mic-btn" onClick={startRecording}>
          <FaMicrophoneIcon
            style={{ minHeight: "18px", minWidth: "18px", color: "#007bff" }}
          />
        </button>
      ) : !isPaused ? (
        <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          <button className="mic-btn" onClick={pauseRecording}>
            <FaPauseIcon
              style={{ minHeight: "18px", minWidth: "18px", color: "#007bff" }}
            />
          </button>
          <button className="mic-btn" onClick={stopRecording}>
            <FaCheckIcon
              style={{ minHeight: "18px", minWidth: "18px", color: "#007bff" }}
            />
          </button>
        </div>
      ) : (
        <button className="mic-btn" onClick={resumeRecording}>
          <FaPlayIcon
            style={{ minHeight: "18px", minWidth: "18px", color: "#007bff" }}
          />
        </button>
      )
    ) : (
      <>
        <div className="mic-btn">
          <Lottie
            animationData={AiLoader}
            autoPlay
            autoplay
            loop
            style={{ height: "24px", width: "24px" }}
          />
        </div>
        <div className="analysing-text">Analysing ...</div>
      </>
    )}
    {timer && timer !== "00:00" && <div className="timer">{timer}</div>}
  </div>
);

// Question field component
const QuestionField = ({ question }: any) => {
  const { question_id, question_type, explanation_text, question_options } =
    question;

  if (question_type === "input" || question_type === "text") {
    return (
      <div className="form-field">
        <Field
          id={question_id}
          name={question_id}
          as={question_type === "text" ? "textarea" : "input"}
          placeholder={explanation_text}
          className="input-field"
        />
        <ErrorMessage
          name={question_id.toString()}
          component="div"
          className="error-message"
        />
      </div>
    );
  }

  if (question_type === "radio") {
    return (
      <div className="form-field-multi">
        {question_options.map((option: any) => (
          <div key={option.key} className="radio-option">
            <Field
              type="radio"
              name={question_id}
              value={option.key}
              id={option.key}
              className="radio-input"
            />
            <label htmlFor={option.key} className="radio-label">
              {option.value}
            </label>
          </div>
        ))}
        <ErrorMessage
          name={question_id.toString()}
          component="div"
          className="error-message"
        />
      </div>
    );
  }

  if (question_type === "multi-select") {
    return (
      <div className="form-field-multi">
        {question_options.map((option: any) => (
          <div key={option.key} className="checkbox-option">
            <Field
              type="checkbox"
              name={question_id}
              value={option.key}
              id={option.key}
              className="checkbox-input"
            />
            <label htmlFor={option.key} className="checkbox-label">
              {option.value}
            </label>
          </div>
        ))}
        <ErrorMessage
          name={question_id.toString()}
          component="div"
          className="error-message"
        />
      </div>
    );
  }

  return null;
};

type FormComponentProps = {
  loggedInUser: { email: string; password: string };
};

const FormComponent: React.FC<FormComponentProps> = ({ loggedInUser }) => {
  const {
    audioBlob,
    isPaused,
    isRecording,
    pauseRecording,
    resumeRecording,
    startRecording,
    stopRecording,
    deleteRecording,
    timer,
  } = useAudioRecording();

  const [data, setData] = useState(initialData);

  const initialValues = React.useMemo(() => {
    return data.reduce((acc, question) => {
      acc[question.question_id] =
        question.question_type === "multi-select" ? [] : "";
      return acc;
    }, {} as Record<string, string | string[]>);
  }, [data]);

  const validationSchema = React.useMemo(() => {
    return generateValidationSchema(data);
  }, [data]);

  const getAnswers = async (audioBlob: Blob) => {
    try {
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.webm");
      formData.append("transcription_model", "gemini");
      formData.append("inference_model", "gemini");
      formData.append("questions", JSON.stringify(data));

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${API_AUTH_TOKEN}`,
        },
        body: formData,
      });

      if (!response.ok) {
        console.error("API call failed:", response.statusText);
        return;
      }

      const result = await response.json();

      if (!result?.answers?.[0]) {
        console.error("Invalid response format");
        return;
      }

      const transformedObject: any = Object.values(result.answers[0]).reduce(
        (acc: any, item: any) => {
          acc[item.question_id] = item.answer;
          return acc;
        },
        {}
      );

      const updatedData = data.map((question) => {
        const answer = transformedObject[question.question_id];
        return answer && answer !== "None"
          ? {
              ...question,
              answer:
                question.question_type === "radio"
                  ? answer.toLowerCase()
                  : answer,
            }
          : question;
      });

      setData(updatedData);
      deleteRecording();
    } catch (error) {
      console.error("Error calling API:", error);
    }
  };

  useEffect(() => {
    if (audioBlob) {
      getAnswers(audioBlob);
    }
  }, [audioBlob]);

  return (
    <div className="form-container">
      <div className="header">
        <h1>A Retrospective, Real-World Observational Study to Evaluate the Safety and Effectiveness of Pantoprazole 40 mg Tablets in Patients Having Heartburn</h1>
        <RecordingControls
          audioBlob={audioBlob}
          isRecording={isRecording}
          isPaused={isPaused}
          startRecording={startRecording}
          pauseRecording={pauseRecording}
          resumeRecording={resumeRecording}
          stopRecording={stopRecording}
          deleteRecording={deleteRecording}
          timer={timer}
        />
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          console.log("Form Submitted with values: ", values);
        }}
      >
        {() => (
          <Form className="form">
            <AutoFillAnswers data={data} />

            {data.map((question) => (
              <div key={question.question_id} className="form-group">
                <div className="form-label-container">
                  <label
                    htmlFor={question.question_id.toString()}
                    className="form-label"
                  >
                    {question.question}
                  </label>
                  <a
                    className={`info-tooltip-${question.question_id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IoIosInformationCircleOutlineIcon
                      style={{
                        height: "18px",
                        width: "18px",
                        color: "#007bff",
                      }}
                    />
                  </a>
                  <Tooltip
                    anchorSelect={`.info-tooltip-${question.question_id}`}
                    place="top-start"
                  >
                    {question.explanation_text}
                  </Tooltip>
                </div>

                <QuestionField question={question} />
              </div>
            ))}
            <button type="submit" className="submit-btn">
              Submit
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default FormComponent;
